import { useApi } from '@classgo/app-kit';
import {
  createClassroomInputSchema,
  createGradeInputSchema,
  createLevelInputSchema,
  createSubjectInputSchema,
  updateClassroomInputSchema,
  updateGradeInputSchema,
  updateLevelInputSchema,
  updateSubjectInputSchema,
} from '@classgo/shared';
import { Input, Select } from '@classgo/ui';

import { academicApi } from '../academic/api';
import type { ClassroomView, GradeView, LevelView, SubjectView } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

const text = (value: string) => (value.trim() === '' ? undefined : value.trim());
const number = (value: string) => (value.trim() === '' ? undefined : Number(value));

/**
 * Create or edit a level (Preescolar, Primaria, Secundaria, Media).
 */
export const LevelForm = ({
  nextPosition,
  current,
  onDone,
}: {
  nextPosition: number;
  current: LevelView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateLevelInputSchema : createLevelInputSchema,
    initial: { name: current?.name ?? '', position: String(current?.position ?? nextPosition) },
    toInput: (values) => ({ name: values.name, position: number(values.position) }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('levels', current.id, input);
            else await academicApi(api).create('levels', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
        <Input
          label={t('levels.field.name')}
          required
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          {...(form.fieldError('name') ? { error: form.fieldError('name') } : {})}
        />
        <Input
          label={t('levels.field.position')}
          inputMode="numeric"
          required
          value={form.values.position}
          onChange={(event) => form.set('position', event.target.value)}
          {...(form.fieldError('position') ? { error: form.fieldError('position') } : {})}
        />
      </div>
    </FormShell>
  );
};

/**
 * Create or edit a grade inside a level.
 */
export const GradeForm = ({
  levels,
  levelId,
  nextPosition,
  current,
  onDone,
}: {
  levels: readonly LevelView[];
  levelId: string;
  nextPosition: number;
  current: GradeView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateGradeInputSchema : createGradeInputSchema,
    initial: {
      levelId: current?.levelId ?? levelId,
      name: current?.name ?? '',
      position: String(current?.position ?? nextPosition),
    },
    toInput: (values) => ({
      levelId: values.levelId,
      name: values.name,
      position: number(values.position),
    }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('grades', current.id, input);
            else await academicApi(api).create('grades', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Select
        label={t('grades.field.level')}
        value={form.values.levelId}
        onChange={(event) => form.set('levelId', event.target.value)}
        options={levels.map((level) => ({ value: level.id, label: level.name }))}
        {...(form.fieldError('levelId') ? { error: form.fieldError('levelId') } : {})}
      />
      <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
        <Input
          label={t('grades.field.name')}
          required
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          {...(form.fieldError('name') ? { error: form.fieldError('name') } : {})}
        />
        <Input
          label={t('grades.field.position')}
          inputMode="numeric"
          required
          value={form.values.position}
          onChange={(event) => form.set('position', event.target.value)}
          {...(form.fieldError('position') ? { error: form.fieldError('position') } : {})}
        />
      </div>
    </FormShell>
  );
};

/**
 * Create or edit a subject.
 */
export const SubjectForm = ({
  current,
  onDone,
}: {
  current: SubjectView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateSubjectInputSchema : createSubjectInputSchema,
    initial: { name: current?.name ?? '', code: current?.code ?? '' },
    toInput: (values) => ({ name: values.name, code: text(values.code) }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('subjects', current.id, input);
            else await academicApi(api).create('subjects', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Input
        label={t('subjects.field.name')}
        required
        value={form.values.name}
        onChange={(event) => form.set('name', event.target.value)}
        {...(form.fieldError('name') ? { error: form.fieldError('name') } : {})}
      />
      <Input
        label={t('subjects.field.code')}
        help={t('subjects.field.code.help')}
        value={form.values.code}
        onChange={(event) => form.set('code', event.target.value)}
        {...(form.fieldError('code') ? { error: form.fieldError('code') } : {})}
      />
    </FormShell>
  );
};

/**
 * Create or edit a classroom of a sede.
 */
export const ClassroomForm = ({
  sedes,
  sedeId,
  current,
  onDone,
}: {
  sedes: readonly { id: string; name: string }[];
  sedeId: string;
  current: ClassroomView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateClassroomInputSchema : createClassroomInputSchema,
    initial: {
      sedeId: current?.sedeId ?? sedeId,
      name: current?.name ?? '',
      capacity: current?.capacity === null || current === null ? '' : String(current.capacity),
    },
    toInput: (values) => ({
      sedeId: values.sedeId,
      name: values.name,
      capacity: number(values.capacity),
    }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('classrooms', current.id, input);
            else await academicApi(api).create('classrooms', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Select
        label={t('classrooms.field.sede')}
        value={form.values.sedeId}
        onChange={(event) => form.set('sedeId', event.target.value)}
        options={sedes.map((sede) => ({ value: sede.id, label: sede.name }))}
        {...(form.fieldError('sedeId') ? { error: form.fieldError('sedeId') } : {})}
      />
      <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
        <Input
          label={t('classrooms.field.name')}
          required
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          {...(form.fieldError('name') ? { error: form.fieldError('name') } : {})}
        />
        <Input
          label={t('classrooms.field.capacity')}
          inputMode="numeric"
          value={form.values.capacity}
          onChange={(event) => form.set('capacity', event.target.value)}
          {...(form.fieldError('capacity') ? { error: form.fieldError('capacity') } : {})}
        />
      </div>
    </FormShell>
  );
};
